"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRegisterRequestMutation = exports.useSubmitRequestMutation = exports.useCancelRequestMutation = exports.useRemoveTagMutation = exports.useAddTagMutation = exports.getRequestQuery = exports.useGetRequestListQuery = void 0;
const react_query_1 = require("@tanstack/react-query");
const strings_1 = require("../constants/strings");
const requestRepository_1 = __importDefault(require("../repositories/requestRepository"));
const zustandStore_1 = require("../zustandStore");
const query_keys_1 = require("./query-keys");
const logger = require('pino')();
const useGetRequestListQuery = () => {
    return (0, react_query_1.useQuery)({
        queryKey: query_keys_1.requestKeys.getRequestList(),
        queryFn: () => requestRepository_1.default.getRequestList(),
        staleTime: Number.POSITIVE_INFINITY, // CHECK: is this the right way to do this?
    });
};
exports.useGetRequestListQuery = useGetRequestListQuery;
const getRequestQuery = (requestId) => ({
    queryKey: [query_keys_1.requestKeys.getRequest, requestId],
    queryFn: () => requestRepository_1.default.getRequest({ requestId }),
    enabled: Boolean(requestId),
});
exports.getRequestQuery = getRequestQuery;
const useAddTagMutation = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: (options) => requestRepository_1.default.addTag(options),
        onMutate: async ({ requestId }) => {
            await queryClient.cancelQueries({ queryKey: [query_keys_1.requestKeys.getRequest, requestId] });
            const previousRequestData = queryClient.getQueryData([query_keys_1.requestKeys.getRequest, requestId]);
            if (previousRequestData) {
                const updatedData = { ...previousRequestData, isArchived: true };
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, requestId], updatedData);
            }
            return { previousRequestData };
        },
        onError: (err, variables, context) => {
            logger.error(err);
            if (context?.previousRequestData) {
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, variables.requestId], context.previousRequestData);
            }
        },
        onSettled: async (_data, error, variables) => {
            if (error) {
                logger.error({ error });
            }
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: [query_keys_1.requestKeys.getRequest, variables.requestId],
                }),
                queryClient.invalidateQueries({
                    queryKey: query_keys_1.requestKeys.getRequestList(),
                }),
            ]);
        },
    });
};
exports.useAddTagMutation = useAddTagMutation;
const useRemoveTagMutation = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: (options) => requestRepository_1.default.removeTag(options),
        onMutate: async ({ requestId }) => {
            const previousRequestData = queryClient.getQueryData([query_keys_1.requestKeys.getRequest, requestId]);
            if (previousRequestData) {
                const updatedData = { ...previousRequestData, isArchived: false };
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, requestId], updatedData);
            }
            return { previousRequestData };
        },
        onSettled: (_data, _error, variables) => Promise.all([
            queryClient.invalidateQueries({
                queryKey: [query_keys_1.requestKeys.getRequest, variables.requestId],
            }),
            queryClient.invalidateQueries({
                queryKey: query_keys_1.requestKeys.getRequestList(),
            }),
        ]),
        onError: (err, variables, context) => {
            logger.error(err);
            if (context?.previousRequestData) {
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, variables.requestId], context.previousRequestData);
            }
        },
    });
};
exports.useRemoveTagMutation = useRemoveTagMutation;
const useCancelRequestMutation = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: (options) => requestRepository_1.default.cancelRequest(options),
        onMutate: async ({ requestId }) => {
            await queryClient.cancelQueries({ queryKey: [query_keys_1.requestKeys.getRequest, requestId] });
            const previousRequestData = queryClient.getQueryData([query_keys_1.requestKeys.getRequest, requestId]);
            if (previousRequestData) {
                const updatedData = { ...previousRequestData, status: 'canceled' };
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, requestId], updatedData);
            }
            return { previousRequestData };
        },
        onError: (err, variables, context) => {
            logger.error(err);
            if (context?.previousRequestData) {
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, variables.requestId], context.previousRequestData);
            }
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({ queryKey: [query_keys_1.requestKeys.getRequest, variables.requestId] });
        },
    });
};
exports.useCancelRequestMutation = useCancelRequestMutation;
const useSubmitRequestMutation = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    const { setNotification } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useMutation)({
        mutationFn: (options) => requestRepository_1.default.submitRequest(options),
        onMutate: async ({ requestId }) => {
            await queryClient.cancelQueries({ queryKey: [query_keys_1.requestKeys.getRequest, requestId] });
            const previousRequestData = queryClient.getQueryData([query_keys_1.requestKeys.getRequest, requestId]);
            if (previousRequestData) {
                const updatedData = { ...previousRequestData, status: 'submitted' };
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, requestId], updatedData);
            }
            return { previousRequestData };
        },
        onError: (error, variables, context) => {
            logger.error({ error });
            if (error?.status === 429) {
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: 'Your company has exceeded the limit for card requests submitted per minute. Please limit requests to less than 5 per minute and try again later.',
                    },
                ]);
            }
            else {
                setNotification([
                    {
                        type: strings_1.notificationStrings.WARNING,
                        message: 'There was a problem submitting the purchase request.',
                    },
                ]);
            }
            if (context?.previousRequestData) {
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, variables.requestId], context.previousRequestData);
            }
        },
        onSettled: (_data, error) => {
            if (error) {
                logger.error({ error });
            }
            queryClient.invalidateQueries({ type: 'active' });
        },
    });
};
exports.useSubmitRequestMutation = useSubmitRequestMutation;
const useRegisterRequestMutation = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: (options) => requestRepository_1.default.registerRequest(options),
        onSettled: (_data, _error, variables) => {
            void queryClient.invalidateQueries({
                queryKey: [query_keys_1.requestKeys.getRequest, variables.requestId],
            });
            // we invalidate the cache so we get the latest data so we know
            // if we have already created a new request in CREATED state
            void queryClient.invalidateQueries({ queryKey: [query_keys_1.requestKeys.getRequestList] });
        },
        onError: (err, variables, context) => {
            logger.error(err);
            if (context?.previousRequestData) {
                queryClient.setQueryData([query_keys_1.requestKeys.getRequest, variables.requestId], context.previousRequestData);
            }
        },
    });
};
exports.useRegisterRequestMutation = useRegisterRequestMutation;
